import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { addMilliseconds, set } from 'date-fns';
import PropTypes from 'prop-types';
import { change, getFormValues } from 'redux-form';

import EventFormComponent from '../components/EventFormComponent';

import { getAmenities } from '../../Amenity/actions';
import { getBuildingsForFilter } from '../../Building/actionCreators';
import { createEvent, getEventData, updateEvent } from '../actions';

import {
  getAmenityFilterOptions,
  getBuildingFilterOptions,
} from '../../Amenity/utils';
import { getUserInfo } from '../../Navigation/utils';
import { calculateDuration, checkCanUserEditEvent } from '../utils';

const form = 'Events';

const EventFormContainer = ({ match }) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const store = useSelector((state) => state);
  const [isEditable, setIsEditable] = useState(true);

  const { buildings, common, amenity, events } = store;
  const { id } = match.params;
  const isCreate = id === 'add';
  const url = '/events/details';

  const formValues = getFormValues(form)(store);
  const {
    customLocationCheckBox: isCustomLocation,
    limitAttendees: isLimitAttendees,
    allowGuests: isAllowGuests,
    buildingId,
    amenityId,
    endTime,
    startTime,
    startDate,
    locationDescription,
    organizer,
    name,
    description,
    category,
    capacity,
    guestsPerResidentLimit,
    image,
  } = formValues || {};

  const currentUserId = common?.currentUser?.id;
  const { event } = events;
  const buildingOptions = getBuildingFilterOptions(buildings);
  const amenities = amenity?.amenities
    ? getAmenityFilterOptions(amenity?.amenities)
    : [];

  useEffect(() => {
    const isEditable = checkCanUserEditEvent(
      currentUserId,
      event?.creator?.id,
      event?.startsAt,
      event?.duration,
    );

    if (event?.creator?.id) {
      setIsEditable(isEditable);
    }
  }, [event?.creator?.id]);

  const updateTime = () => {
    const year = new Date(startDate).getFullYear();
    const month = new Date(startDate).getMonth();
    const date = new Date(startDate).getDate();

    if (!year || !month || !date) return;

    const updatedStartTime = set(new Date(startTime || startDate), {
      year,
      month,
      date,
    });
    const updatedEndTime = set(new Date(endTime || startDate), {
      year,
      month,
      date,
    });

    dispatch(change(form, 'startTime', updatedStartTime));
    dispatch(change(form, 'endTime', updatedEndTime));
  };

  useEffect(() => {
    updateTime();
  }, [startDate]);

  const defaultValues = {
    limitAttendees: false,
    allowGuests: false,
    customLocationCheckBox: false,
  };

  const initialValues = {
    name: event?.name,
    organizer: event?.organizer,
    startDate: event?.startsAt && new Date(event?.startsAt).toISOString(),
    startTime: event?.startsAt && new Date(event?.startsAt).toISOString(),
    endTime:
      event?.startsAt &&
      addMilliseconds(new Date(event?.startsAt), event?.duration).toISOString(),
    buildingId: event?.buildingId,
    amenityId: event?.amenity?.id,
    customLocationCheckBox: !!event?.locationDescription,
    locationDescription: event?.locationDescription,
    description: event?.description,
    category: event?.categories?.toString(),
    limitAttendees: !!event?.capacity,
    allowGuests: !!event?.guestsPerResidentLimit,
    guestsPerResidentLimit: event?.guestsPerResidentLimit,
    capacity: event?.capacity,
    image: event.photo && [event?.photo],
  };

  const onLoad = useCallback(() => {
    getBuildingsForFilter(dispatch);
    getUserInfo(dispatch);
  }, []);

  const onSubmit = async () => {
    let response;

    const duration = calculateDuration(startTime, endTime);

    const data = {
      buildingId,
      amenityId,
      locationDescription,
      creatorId: currentUserId,
      organizer,
      name,
      description,
      startsAt: startTime,
      duration,
      categories: [category],
      capacity,
      guestsPerResidentLimit,
    };

    if (isCreate) {
      response = await createEvent(dispatch, data, image);
    } else {
      response = await updateEvent(dispatch, id, data, image);
    }

    if (response) history.goBack();
  };

  useEffect(() => {
    dispatch(
      change(
        form,
        isCustomLocation ? 'amenityId' : 'locationDescription',
        isCustomLocation ? null : '',
      ),
    );
  }, [isCustomLocation]);

  useEffect(() => {
    if (!isAllowGuests) {
      dispatch(change(form, 'guestsPerResidentLimit', 0));
    }
  }, [isAllowGuests]);

  useEffect(() => {
    if (!isLimitAttendees) {
      dispatch(change(form, 'capacity', 0));
    }
  }, [isLimitAttendees]);

  useEffect(() => {
    if (isCreate) return;
    getEventData(dispatch, id);
  }, []);

  useEffect(() => {
    if (!buildingId) return;

    getAmenities(dispatch, {
      building: buildingId,
      disable_pagination: true,
    });
  }, [buildingId]);

  return (
    <EventFormComponent
      url={url}
      form={form}
      onLoad={onLoad}
      onSubmit={onSubmit}
      isCreate={isCreate}
      endTime={endTime}
      startTime={startTime}
      amenities={amenities}
      buildingId={buildingId}
      amenityId={amenityId}
      buildings={buildingOptions}
      initialValues={isCreate ? defaultValues : initialValues}
      isAllowGuests={isAllowGuests}
      isCustomLocation={isCustomLocation}
      isLimitAttendees={isLimitAttendees}
      isEventEditable={isEditable}
    />
  );
};

EventFormContainer.propTypes = {
  match: PropTypes.instanceOf(Object).isRequired,
};

export default EventFormContainer;
